import type { ReactNode } from 'react';
import { classNames } from '@/lib/utils';

interface CreationLayoutProps {
  children: ReactNode;
  /** Optional back button rendered top-left. */
  onBack?: () => void;
  backLabel?: string;
  /** Disable the stage fade-in animation (e.g. on the transition stage). */
  noFade?: boolean;
  /** Stage key used to retrigger the fade-in animation when the stage changes. */
  stageKey?: string;
}

export function CreationLayout({
  children,
  onBack,
  backLabel = 'Back',
  noFade = false,
  stageKey,
}: CreationLayoutProps): JSX.Element {
  return (
    <main className="relative min-h-dvh px-4 py-8 sm:py-12 flex items-center justify-center overflow-hidden">
      {/* Subtle atmospheric vignette over the body background. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(20, 50, 71, 0.0) 0%, rgba(12, 34, 51, 0.55) 80%)',
        }}
      />

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10 inline-flex items-center gap-1.5 rounded-md border border-parchment/20 bg-sea-dark/60 px-3 py-2 text-xs font-display uppercase tracking-wider text-parchment-dark hover:border-candle hover:text-candle transition-colors min-h-11"
        >
          <span aria-hidden>&larr;</span>
          <span>{backLabel}</span>
        </button>
      )}

      <div
        key={stageKey}
        className={classNames('relative w-full max-w-xl', !noFade && 'stage-fade-in')}
      >
        <div className="rounded-lg border border-wood-mid/40 bg-parchment text-ink shadow-2xl shadow-black/50">
          {children}
        </div>
      </div>
    </main>
  );
}
